/**
 * Communication — phase 1.
 *
 * Tabbed page with two sub-tabs (Inbox + Calendar). Both are heavy-ish, so
 * they live in their own files; this page just owns the hero header,
 * sub-tab nav, and mount-routing.
 *
 * The `CommunicationGuard` route wrapper at the bottom declines non-granted
 * users (matching the Support pattern) before mounting the inbox.
 */
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Inbox as InboxIcon, MessageSquare, ShieldAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState } from 'react';

import { useAuth } from '../../hooks/useAuth';
import { apiRequest } from '../../lib/api-client';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query-keys';

import { CalendarPane } from './CalendarPane';
import { InboxPane } from './InboxPane';
import type { IconLike } from './kind-meta';

type SubTab = 'inbox' | 'calendar';

export function CommunicationPage(): JSX.Element {
  const [tab, setTab] = useState<SubTab>('inbox');

  return (
    <div className="flex h-full flex-col">
      {/* Hero header — matches the pattern used by Support / CRM / Video Editor */}
      <header className="flex items-center justify-between gap-4 border-b border-border bg-surface px-6 py-3">
        <div className="flex items-center gap-3">
          <div className="grid size-9 place-items-center rounded-sm bg-accent/10 text-accent">
            <MessageSquare className="size-4" />
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-tight text-muted">
              Communication
            </p>
            <h1 className="text-lg font-semibold tracking-tight text-text">
              Unified inbox
            </h1>
          </div>
        </div>
      </header>

      {/* Sub-tab nav */}
      <nav className="flex items-center gap-1 border-b border-border bg-bg/30 px-6 py-2">
        <SubTabButton
          active={tab === 'inbox'}
          onClick={() => setTab('inbox')}
          label="Inbox"
          Icon={InboxIcon}
        />
        <SubTabButton
          active={tab === 'calendar'}
          onClick={() => setTab('calendar')}
          label="Calendar"
          Icon={CalendarDays}
        />
      </nav>

      <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
        {tab === 'inbox' && <InboxPane />}
        {tab === 'calendar' && <CalendarPane />}
      </div>
    </div>
  );
}

function SubTabButton({
  active,
  onClick,
  label,
  Icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  Icon: IconLike | LucideIcon;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-xs font-medium transition',
        active
          ? 'bg-accent/10 text-text'
          : 'text-muted hover:bg-bg hover:text-text',
      )}
    >
      <Icon className="size-3.5" />
      {label}
    </button>
  );
}

// Re-export useAuth-shaped guard so the route can decline non-granted users
// before mounting the heavy inbox. (Optional but matches the Support pattern.)
export function CommunicationGuard({ children }: { children: JSX.Element }): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const accessQuery = useQuery({
    queryKey: queryKeys.communication.access(),
    queryFn: () => apiRequest<{ access: boolean }>('/communication/access'),
    enabled: !isAdmin,
  });
  if (isAdmin) return children;
  if (accessQuery.isLoading) {
    return (
      <div className="grid h-full place-items-center text-sm text-muted">Checking access…</div>
    );
  }
  if (!accessQuery.data?.access) {
    return (
      <div className="grid h-full place-items-center text-center">
        <div className="max-w-md space-y-2">
          <ShieldAlert className="mx-auto size-6 text-muted" />
          <p className="text-sm text-muted">
            You don&apos;t have access to any Communication channel yet. Ask an admin to grant you
            on a specific channel.
          </p>
        </div>
      </div>
    );
  }
  return children;
}
